import { formatDate, t } from '@unistocks/localization';
import { Logger } from '@unistocks/shared/services/logger.service';
import { arrayify, createDate, isDefined, isNumber } from '@unistocks/shared/utils/util';
import { MaybeArray } from '@unistocks/types';
import { z } from 'zod';

export type ValidatorOptions = {
  label?: string;
};

export type ObjectRefinement<TValues> = (values: TValues, ctx: z.RefinementCtx) => void;

const numberInput = z.union([z.number(), z.string()]).nullish();
const dateInput = z.union([z.date(), z.string()]).nullish();

/**
 * Validates if the value is a number, allows null and empty string
 * @returns number schema
 */
export const createNumberSchema = (_options: ValidatorOptions = {}) =>
  numberInput.transform((value, ctx) => {
    if (isNumber(value)) return value;
    if (!isDefined(value)) return null;

    try {
      const valueAsNumber = Number(value.replace(',', '.'));

      if (isNumber(valueAsNumber)) return valueAsNumber;
    } catch (e) {
      Logger.error(e, `Failed to parse number: ${value}`);
    }

    ctx.addIssue({ code: 'custom', message: t('::ValidationError:Number') });

    return z.NEVER;
  });

export const createMoreThanSchema = (min: number, label: string) =>
  createNumberSchema().refine(
    value => value === null || value > min,
    t('::ValidationError:LargerThan', { 0: label, 1: min })
  );

/**
 * Requires a value. Rejects null, undefined, empty strings and NaN.
 */
export const requiredSchema = <TSchema extends z.ZodType>(
  schema: TSchema,
  label?: string,
  message?: string
) =>
  schema.refine(
    isDefined,
    message ??
      (label
        ? t('::The {0} field is required.', { 0: label })
        : t('::ThisFieldIsRequired.'))
  );

export type ConditionalRequiredOptions<TValues> = {
  /**
   * Fields the condition depends on
   */
  dependsOn?: MaybeArray<keyof TValues>;
  /**
   * Field name used in the default validation message
   */
  fieldName?: string;
  /**
   * Custom validation message
   */
  message?: string;
};

/**
 * Object level refinement that requires a field when the condition is met.
 * Use with {@link applyRefinements} since sibling values are only available at the
 * object level in zod.
 */
export const conditionalRequired = <TValues extends Record<string, any> = Record<string, any>>(
  condition: (...params: any[]) => boolean,
  field: keyof TValues & string,
  { dependsOn, fieldName, message }: ConditionalRequiredOptions<TValues> = {}
): ObjectRefinement<TValues> =>
  (values, ctx) => {
    const params = dependsOn ? arrayify(dependsOn).map(key => values[key]) : [];

    if (!condition(...params)) return;
    if (isDefined(values[field])) return;

    ctx.addIssue({
      code: 'custom',
      path: [field],
      message: message ?? t('::The {0} field is required.', { 0: fieldName ?? field })
    });
  };

/**
 * Applies one or more object level refinements to an object schema.
 */
export const applyRefinements = <TSchema extends z.ZodObject<any>>(
  schema: TSchema,
  ...refinements: ObjectRefinement<z.output<TSchema>>[]
) =>
  refinements.length
    ? schema.superRefine((values, ctx) => refinements.forEach(refinement => refinement(values, ctx)))
    : schema;

/**
 * Validates that a value is one of the provided options
 */
export const oneOf = <const TValues extends readonly (string | number)[]>(
  values: TValues,
  message?: string
) => z.literal(values, message);

export type DateValidatorOptions = ValidatorOptions & {
  min?: Date;
  max?: Date;
};

/**
 * Validation schema for date picker fields.
 * @returns ISO date string schema
 */
export const createDateSchema = ({ label, min, max }: DateValidatorOptions = {}) =>
  dateInput
    .transform((value, _ctx) => {
      if (!isDefined(value)) return null;
      if (typeof value === 'string') return value;

      try {
        // createDate clears the time of the Date object
        // If saving time information is needed, create a dateTimeSchema
        return createDate(value).toISODate();
      } catch (e) {
        Logger.debug(`Failed to parse date: ${value}`, e);

        return null;
      }
    })
    .superRefine((value, ctx) => {
      // Only validate if the value is defined. This is not a required schema
      if (!isDefined(value)) return;

      try {
        const date = createDate(value);

        if (!date?.isValid) return;

        if (min && date < createDate(min)) {
          ctx.addIssue({
            code: 'custom',
            message: t('::ValidationError:Date:Min', { 0: label ?? t('::Date'), 1: formatDate(min) })
          });
        }

        if (max && date > createDate(max)) {
          ctx.addIssue({
            code: 'custom',
            message: t('::ValidationError:Date:Max', { 0: label ?? t('::Date'), 1: formatDate(max) })
          });
        }
      } catch (e) {
        Logger.debug(`Failed to validate date: ${value}`, e);
      }
    });
